/**
 * Route user requests to the appropriate LLM tier.
 *
 * Inspects user input to decide command tier (single action) vs
 * workflow tier (multi-step), builds the prompt, sends to backend,
 * and parses the response.
 *
 * @module gibbs/text-to-action/routeRequest
 */

import { buildCommandPrompt, buildWorkflowPrompt } from './buildPrompt';
import { sendLLMRequest } from './llmClient';
import { parseResponse } from './parseResponse';
import { retrieveDocs } from './docRetriever';
import { serializeWorkspace } from './serializeWorkspace';
import { filterForCommandTier, filterForWorkflowTier } from './filterActions';
import { serializePreferencesContext } from '../preferences';
import { serializeHistoryContext } from '../conversationHistory';
import { getAllActions } from '../registry/index';
import { logLLMCall } from '../telemetry';

/**
 * Find registered actions whose IDs are similar to a hallucinated one.
 * Uses substring matching + word overlap — not a full fuzzy search,
 * but enough to turn "rotate" → "rotate-view" and "grow-from-fragment"
 * → "grow-from-atom".
 *
 * @param {string} hallucinated - The actionId the LLM invented
 * @param {number} [maxResults=3]
 * @returns {Array<{id: string, name: string, description: string}>}
 */
function findSimilarActions(hallucinated, maxResults = 3) {
    const actions = getAllActions();
    const lower = hallucinated.toLowerCase();
    const words = lower.split(/[-_\s]+/).filter((w) => w.length > 2);

    const scored = actions.map((action) => {
        const aid = action.id.toLowerCase();
        let score = 0;

        // Substring containment (either direction)
        if (aid.includes(lower) || lower.includes(aid)) score += 10;

        // Word overlap
        const actionWords = aid.split('-');
        for (const w of words) {
            if (actionWords.some((aw) => aw.includes(w) || w.includes(aw))) {
                score += 3;
            }
        }

        // Name/description keyword match
        const nameLower = (action.name || '').toLowerCase();
        const descLower = (action.description || '').toLowerCase();
        for (const w of words) {
            if (nameLower.includes(w)) score += 2;
            if (descLower.includes(w)) score += 1;
        }

        return { ...action, score };
    });

    return scored
        .filter((a) => a.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults)
        .map(({ id, name, description }) => ({ id, name, description }));
}

/**
 * Keywords that suggest a multi-step workflow request.
 */
const WORKFLOW_INDICATORS = [
    'workflow', 'optimize', 'hit-to-lead', 'lead optimization',
    'selectivity', 'screen', 'screening', 'pipeline',
    'then', 'after that', 'followed by', 'step by step',
    'and also', 'while keeping', 'while maintaining',
    'multiple', 'series of', 'sequence of',
    // Multi-operation patterns that don't use explicit sequencing words
    // but still imply a workflow.
    'and rank', 'and score', 'and predict', 'and filter',
    'and evaluate', 'and compare',
    'for each', 'for all', 'for every',
    'minimize and', 'dock and', 'load and',
    // Hit discovery / fragment screening patterns
    'hit discovery', 'discover hits', 'discovery hits',
    'find hits', 'find new compounds', 'screen fragments',
    'fragment screen', 'grow and score', 'grow and minimize',
    // Virtual screening / library screening
    'virtual screen', 'library screen', 'screen my library',
    // De novo generation
    'de novo', 'denovo', 'reinvent', 'generative design',
    'generate compounds', 'generate molecules',
];

/**
 * Determine whether a user message likely needs the workflow tier.
 *
 * @param {string} userMessage
 * @returns {boolean}
 */
export function isWorkflowRequest(userMessage) {
    const lower = userMessage.toLowerCase();
    return WORKFLOW_INDICATORS.some((kw) => lower.includes(kw));
}

/**
 * @typedef {Object} RetryContext
 * @property {Object} previousResponse - The previous classified response that was rejected
 * @property {string[]} errors - Validator errors from the previous attempt
 * @property {number} attempt - 1-based attempt number (first retry is attempt=1)
 */

/**
 * Route a user request through the text-to-action pipeline.
 *
 * 1. Determine tier (command vs workflow)
 * 2. Build the appropriate prompt
 * 3. Optionally append retry-correction messages (on re-entry after a
 *    validation failure). The retry messages simulate an assistant
 *    turn (the rejected plan) + a user turn (the validator's errors)
 *    so the LLM can see its own mistake and produce a correction.
 * 4. Send to LLM
 * 5. Parse and return classified intent
 *
 * @param {string} userMessage - Natural language input
 * @param {Object} workspace - BMaps workspace state
 * @param {RetryContext} [retryContext] - Optional retry correction context
 * @param {{ anchoredProtein?: Object }} [extras] - Optional pre-computed
 *   data from upstream routing steps (e.g., the anchor resolver). When
 *   `extras.anchoredProtein` is set, buildPrompt surfaces it in the
 *   system prompt so the LLM is grounded on the verified target.
 * @returns {Promise<{ classified: Object, tier: string, parseErrors: string[] }>}
 */
export async function routeRequest(userMessage, workspace, retryContext, extras = {}) {
    const tier = isWorkflowRequest(userMessage) ? 'workflow' : 'command';

    // Parallelize doc retrieval with context gathering — these are
    // independent operations. Doc retrieval can take 2+ seconds if
    // the service is slow; no reason to block context serialization.
    const [docChunks, userContext] = await Promise.all([
        retrieveDocs(userMessage, tier),
        Promise.resolve({
            preferences: serializePreferencesContext(),
            history: serializeHistoryContext(5),
        }),
    ]);

    // Pre-compute workspace state + filtered actions ONCE.
    // Both are reused by buildPrompt internally; passing them via
    // `cached` avoids redundant serialization and filtering.
    const workspaceState = serializeWorkspace(workspace);
    const actions = tier === 'workflow'
        ? filterForWorkflowTier(userMessage)
        : filterForCommandTier(workspace, userMessage);
    const cached = {
        workspaceState,
        actions,
        anchoredProtein: extras.anchoredProtein || null,
    };

    const prompt = tier === 'workflow'
        ? buildWorkflowPrompt(userMessage, workspace, docChunks, userContext, cached)
        : buildCommandPrompt(userMessage, workspace, docChunks, userContext, cached);

    // On retry, append a simulated conversational exchange showing
    // the LLM its own rejected output and the validator's errors.
    // When the error is a hallucinated actionId, we fuzzy-match it
    // against the registry and include the top suggestions so the
    // LLM doesn't have to scan 127+ actions to find the right one.
    if (retryContext) {
        const { previousResponse, errors, attempt = 1 } = retryContext;
        const assistantTurn = {
            role: 'assistant',
            content: JSON.stringify(previousResponse),
        };

        // Build error list with fuzzy suggestions for unknown actionIds
        const errorLines = (errors || []).map((e, i) => {
            let line = `${i + 1}. ${e}`;
            // If the error mentions an unknown actionId, suggest similar ones
            const unknownMatch = e.match(/unknown actionId "([^"]+)"/);
            if (unknownMatch) {
                const suggestions = findSimilarActions(unknownMatch[1]);
                if (suggestions.length > 0) {
                    line += '\n   Did you mean one of these?\n';
                    line += suggestions
                        .map((s) => `   - "${s.id}" (${s.name}: ${s.description.slice(0, 80)}...)`)
                        .join('\n');
                }
            }
            return line;
        });
        const errorList = errorLines.join('\n');

        const correctionTurn = {
            role: 'user',
            content: 'The plan you returned was rejected by the validator '
                + `with ${errors.length} error${errors.length === 1 ? '' : 's'}:\n\n`
                + `${errorList}\n\n`
                + 'Please return a corrected plan. Rules for the correction:\n'
                + '- Only use actionIds that appear in the "Available Actions" '
                + 'list in the system prompt above. Do not invent new action IDs.\n'
                + '- If a "Did you mean" suggestion is listed above, use that '
                + 'exact actionId.\n'
                + '- Workspace literals ("active", "current", "all") must go in '
                + '`resolvedInputs`, never in `inputsFrom`.\n'
                + '- Step-to-step references must use the format '
                + '"step:N.outputName" in `inputsFrom`, with N referring to an '
                + 'earlier step in the same workflow.\n'
                + '- Respect the type compatibility rules and precondition '
                + 'requirements of each action.\n'
                + `- This is retry attempt ${attempt}. Do not repeat the same `
                + 'mistake; look at the specific errors above.\n\n'
                + 'Return only the corrected JSON, no commentary.',
        };
        prompt.messages.push(assistantTurn, correctionTurn);
    }

    const t0 = Date.now();

    const llmResponse = await sendLLMRequest({
        messages: prompt.messages,
        model: prompt.model,
        jsonMode: true,
    });

    const durationMs = Date.now() - t0;

    const { classified, parseErrors } = parseResponse(llmResponse);

    // Fire-and-forget telemetry — never block the pipeline
    const systemMsg = prompt.messages.find((m) => m.role === 'system');
    logLLMCall({
        userMessage,
        tier,
        systemPromptLength: systemMsg ? systemMsg.content.length : 0,
        messagesCount: prompt.messages.length,
        model: prompt.model,
        responseContent: llmResponse.content || JSON.stringify(llmResponse.parsed),
        resultType: classified ? classified.intent || 'unknown' : 'unknown',
        retryAttempt: retryContext?.attempt || 0,
        parseErrors: parseErrors || [],
        validationErrors: [],
        duration_ms: durationMs,
    }).catch(() => {});

    return { classified, tier, parseErrors };
}
